/**
 * How far a visitor gets through sign-up, counted by the API (routes/signup_steps.py; PostHog is out of credit).
 * Each step is sent once a visit (sessionStorage), with the device and the page on this site the visitor came to
 * sign-up from, and nothing else. Sent as text/plain with keepalive, so it needs no CORS preflight and survives the
 * page leaving for Google. Never throws.
 */
import { config } from '../config';
import { deviceTimeZone } from './blockedRegion';

const SENT = 'signup.steps.sent';
const FROM = 'signup.steps.from';

/** The sign-up path's own steps, by Clerk's routes under /signup. */
export function stepForPath(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '');
  if (path.endsWith('/sso-callback')) return 'google_back';
  if (path.endsWith('/verify-email-address')) return 'email_code';
  if (path.endsWith('/continue')) return 'continue';
  return null;
}

export function deviceOf(userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '') {
  return /Mobi|Android|iPhone|iPad/i.test(userAgent) ? 'phone' : 'desktop';
}

/**
 * The page this visit landed on (index.jsx calls it at start, before any route change): a visit moves between
 * pages inside the app, where document.referrer never changes, so the landing page is what led to sign-up.
 * '/signup' when the visit began there.
 */
export function rememberLanding(storage = session(), pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  try {
    if (storage && storage.getItem(FROM) === null) storage.setItem(FROM, pathname || '/');
  } catch {
    // storage refused
  }
}

export function fromPage(storage) {
  try {
    return storage?.getItem(FROM) || '';
  } catch {
    return '';
  }
}

function session() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Sends `step` once a visit. Returns whether it was sent. */
export function signupStep(step, { storage = session(), fetchImpl = typeof fetch !== 'undefined' ? fetch : null, base = config.API_URL } = {}) {
  if (!step || !fetchImpl || !base) return false;
  let sent = [];
  try {
    sent = JSON.parse(storage?.getItem(SENT) || '[]');
  } catch {
    sent = [];
  }
  if (sent.includes(step)) return false;
  try {
    storage?.setItem(SENT, JSON.stringify([...sent, step]));
  } catch {
    // counted again on the next page at worst
  }
  // The not-available card shown for a time zone also says which zone (the API counts them apart)
  const zone = /unavailable_zone$/.test(step) ? deviceTimeZone() : undefined;
  const body = JSON.stringify({ step, device: deviceOf(), page: fromPage(storage) || '/', ...(zone && { zone }) });
  try {
    fetchImpl(`${base.replace(/\/$/, '')}/public/signup-steps`, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'text/plain' },
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch {
    // a count, never an error
  }
  return true;
}

/** The step a click inside Clerk's sign-up card is: Google's button, or the form's own Continue. */
export function stepForClick(target) {
  const el = target?.closest?.('button, a');
  if (!el) return null;
  const cls = String(el.className || '');
  if (/socialButtonsBlockButton__google|socialButtonsIconButton__google/.test(cls)) return 'google';
  if (/formButtonPrimary/.test(cls)) return 'email';
  return null;
}

/** The steps already sent this visit. */
export function sentSteps(storage = session()) {
  try {
    return JSON.parse(storage?.getItem(SENT) || '[]');
  } catch {
    return [];
  }
}

/**
 * What a sign-in that just finished came through, from the steps this visit sent: 'google' (back from Google's
 * page after Google was pressed: the page loaded signed in, or Clerk's callback route), 'onetap' (One Tap was
 * offered and the form's own email was not used: One Tap signs in on the page, or reloads it signed in, which is
 * how most of its sign-ins arrived: 217 Google accounts on 2026-10-24, 93 counted), or null.
 */
export function signInSource({ sent = [], pathname = '', atLoad = false }) {
  const pressedGoogle = sent.includes('google') || sent.includes('login_google');
  const offered = sent.includes('onetap_offered');
  if (atLoad || /sso-callback/.test(pathname)) return pressedGoogle ? 'google' : offered ? 'onetap' : null;
  const onForm = /^\/(signup|login)(\/|$)/.test(pathname);
  if (onForm && (sent.includes('email') || sent.includes('login_email'))) return null;
  return sent.includes('onetap_offered') ? 'onetap' : null;
}
